import React, { useEffect, useState } from 'react';
import { Loader2, Save, Eye, EyeOff } from 'lucide-react';

const backendUrl = process.env.REACT_APP_BACKEND_URL;

const FIELDS = [
  ['client_goals',          "Client's goals"],
  ['current_situation',     'Current situation'],
  ['recommended_structure', 'Recommended structure'],
  ['why_lender_selected',   'Why this lender'],
  ['risks_considerations',  'Risks & considerations'],
  ['long_term_plan',        'Long-term plan'],
  ['next_steps',            'Next steps'],
  ['broker_notes',          'Broker notes (also visible to client)'],
];

export default function AdminStrategyPanel({ clientId, token }) {
  const auth = { Authorization: `Bearer ${token}` };
  const [form, setForm] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [published, setPublished] = useState(false);

  const reload = async () => {
    const r = await fetch(`${backendUrl}/api/admin/clients/${clientId}/strategy`, { headers: auth });
    if (r.ok) {
      const d = await r.json();
      setForm(d || {});
      setPublished(!!d?.published);
    }
  };

  useEffect(() => {
    (async () => { try { await reload(); } finally { setLoading(false); } })();
    // eslint-disable-next-line
  }, [clientId]);

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const save = async (publishFlag) => {
    setSaving(true);
    try {
      const body = {};
      for (const [k] of FIELDS) body[k] = form[k] ?? null;
      const flag = publishFlag !== undefined ? publishFlag : published;
      const url = `${backendUrl}/api/admin/clients/${clientId}/strategy?published=${flag ? 'true' : 'false'}`;
      await fetch(url, { method: 'PUT', headers: { ...auth, 'Content-Type': 'application/json' }, body: JSON.stringify(body) });
      setPublished(flag);
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <div className="flex justify-center py-12"><Loader2 className="w-5 h-5 animate-spin text-slate-400" /></div>;

  return (
    <div className="p-5 space-y-3">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <span className="text-xs uppercase tracking-wide text-slate-500 font-semibold">Strategy</span>
        <span className={`px-2 py-0.5 rounded-full ring-1 text-[11px] ${published ? 'bg-emerald-50 text-emerald-700 ring-emerald-200' : 'bg-slate-100 text-slate-700 ring-slate-200'}`}>
          {published ? 'Published to client' : 'Draft (hidden from client)'}
        </span>
      </div>

      {FIELDS.map(([k, label]) => (
        <label key={k} className="block text-sm">
          <span className="block text-slate-600 mb-1 text-xs uppercase tracking-wide">{label}</span>
          <textarea
            rows={3}
            value={form[k] || ''}
            onChange={(e) => update(k, e.target.value)}
            data-testid={`strategy-${k}`}
            className="w-full px-2.5 py-1.5 rounded-md ring-1 ring-slate-200 bg-white focus:outline-none focus:ring-2 focus:ring-emerald-400 text-sm"
          />
        </label>
      ))}

      <div className="pt-3 border-t border-slate-100 flex justify-end gap-2">
        <button onClick={() => save(false)} disabled={saving} className="px-3 py-2 text-sm rounded-lg ring-1 ring-slate-200 text-slate-700 hover:bg-slate-50 inline-flex items-center gap-1.5">
          <EyeOff className="w-3.5 h-3.5" /> Save as draft
        </button>
        <button onClick={() => save(true)} disabled={saving} data-testid="publish-strategy" className="px-4 py-2 text-sm rounded-lg bg-emerald-500 hover:bg-emerald-600 text-white font-medium inline-flex items-center gap-1.5">
          {saving && <Loader2 className="w-3.5 h-3.5 animate-spin" />}
          <Eye className="w-3.5 h-3.5" /> Publish to client
        </button>
      </div>
    </div>
  );
}
